import React, { useState } from 'react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { Wrench, Upload, X, Check, AlertCircle, Phone, Mail, Camera, FileText, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../services/api';

const RollerTeamParts = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [photos, setPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    chassisNumber: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    partsDescription: '',
  });

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AutoPartsStore",
    "name": "Millie's Motorhome Hire - Roller Team Genuine Parts",
    "description": "Official Roller Team genuine parts supplier in Suffolk, UK. We supply authentic replacement parts for all Roller Team motorhome models including Zefiro, T-Line, Pegaso, and Auto-Roller.",
    "url": "https://milliesmotorhomehire.co.uk/roller-team-parts",
    "telephone": "+447527006966",
    "email": "milliesmotorhomehire@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Wattisham",
      "addressRegion": "Suffolk",
      "postalCode": "IP7 7JS",
      "addressCountry": "GB"
    },
    "areaServed": { "@type": "Country", "name": "United Kingdom" },
    "brand": { "@type": "Brand", "name": "Roller Team" }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Where can I buy genuine Roller Team parts in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Millie's Motorhome Hire in Suffolk supplies genuine Roller Team parts for all models. We offer fast UK delivery and expert advice."
        }
      },
      {
        "@type": "Question",
        "name": "What Roller Team models do you supply parts for?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We supply genuine parts for all Roller Team models including Zefiro, T-Line, Pegaso, Auto-Roller, Granduca, and Livingstone series."
        }
      },
      {
        "@type": "Question",
        "name": "Do you supply Roller Team spares to the whole of the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes - we post Roller Team spare parts to every part of the UK, and customers in Suffolk, Essex, Norfolk and Cambridgeshire can collect from us at Wattisham."
        }
      },
      {
        "@type": "Question",
        "name": "Can you identify a Roller Team part from a photo?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes - WhatsApp a photo of the part, where it fits and your chassis number to 07527 006966 and we will usually identify it the same day."
        }
      }
    ]
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024);
    setPhotos([...photos, ...validFiles].slice(0, 5));
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('customer_name', formData.customerName);
      formDataToSend.append('customer_email', formData.customerEmail);
      formDataToSend.append('customer_phone', formData.customerPhone);
      formDataToSend.append('chassis_number', formData.chassisNumber);
      formDataToSend.append('vehicle_make', formData.vehicleMake);
      formDataToSend.append('vehicle_model', formData.vehicleModel);
      formDataToSend.append('vehicle_year', formData.vehicleYear);
      formDataToSend.append('parts_description', formData.partsDescription);
      photos.forEach((photo) => formDataToSend.append('photos', photo));

      const response = await api.post('/parts/enquiry', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({ customerName: '', customerEmail: '', customerPhone: '', chassisNumber: '', vehicleMake: '', vehicleModel: '', vehicleYear: '', partsDescription: '' });
      setPhotos([]);
    } catch (err) {
      setSubmitStatus({ type: 'error', message: err.response?.data?.detail || 'An error occurred.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.customerName && formData.customerEmail && formData.customerPhone &&
    formData.chassisNumber && formData.vehicleMake && formData.vehicleModel && 
    formData.vehicleYear && formData.partsDescription;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="roller-team-parts-page">
      {/* Ultimate SEO */}
      <SEO
        title="Roller Team Parts & Spares UK | Genuine Roller Team Spare Parts Supplier"
        rawTitle
        description="Genuine Roller Team spares and parts in the UK from Suffolk-based Roller Team specialists. Spare parts for Zefiro, T-Line, Pegaso, Auto-Roller, Kronos & older models - lockers, windows, trims, blinds, habitation parts. WhatsApp photos for fast ID & UK-wide delivery."
        keywords="Roller Team spares, Roller Team parts, Roller Team spare parts UK, genuine Roller Team parts, Roller Team motorhome parts, Zefiro spares, T-Line spares, Pegaso parts, Auto-Roller parts, Roller Team locker door, Roller Team window, Roller Team habitation parts, Roller Team parts supplier"
        url="/roller-team-parts"
        image="https://customer-assets-v7afamib.emergentagent.net/job_39775d55-8769-4a7d-8ca7-65bbba50c556/artifacts/u0sybjmf_Zefiro%20675_011_SB_5590.webp"
        structuredData={[structuredData, faqStructuredData, generateBreadcrumbSchema([{ name: 'Roller Team Parts & Spares', path: '/roller-team-parts' }])]}
      />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-red-500/20 px-4 py-2 rounded-full mb-6">
                <Wrench className="h-5 w-5 text-red-400" />
                <span className="text-red-300 font-medium">Spare Parts Supplier</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Roller Team Parts &amp; Spares UK</h1>
              <p className="text-xl text-slate-300 mb-8">
                Genuine Roller Team spare parts for every model, supplied by Suffolk's Roller Team specialists. Send us a photo and your chassis number for a fast quote - UK-wide delivery.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <Check className="h-6 w-6 text-green-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-sm">Genuine Parts</h3>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <Truck className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-sm">UK-Wide Delivery</h3>
                  <p className="text-xs text-slate-300 mt-1">Italy-sourced parts approx. 8 weeks</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <Phone className="h-6 w-6 text-amber-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-sm">Expert Advice</h3>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <img src="https://customer-assets-v7afamib.emergentagent.net/job_39775d55-8769-4a7d-8ca7-65bbba50c556/artifacts/u0sybjmf_Zefiro%20675_011_SB_5590.webp" alt="Roller Team Zefiro Motorhome" className="w-full rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Parts Categories */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Roller Team Parts We Supply</h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-center mb-8">Parts for all Roller Team models: Zefiro, T-Line, Pegaso, Auto-Roller, Granduca, Livingstone</p>
          <div className="grid md:grid-cols-4 gap-4">
            {['Body Panels', 'Windows & Doors', 'Lighting', 'Electrical', 'Plumbing', 'Gas Systems', 'Interior Trim', 'Chassis Parts'].map((cat, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors text-center">
                <h3 className="font-semibold text-slate-900">{cat}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Models */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">All Roller Team Models We Cover</h2>
            <p className="text-slate-600">Genuine parts available for every Roller Team motorhome from 2002 to present</p>
          </div>
          
          {/* Model Ranges */}
          <div className="space-y-6">
            {/* Zefiro Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Premium</span>
                Zefiro Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Zefiro 265', 'Zefiro 266', 'Zefiro 267', 'Zefiro 274', 'Zefiro 275', 'Zefiro 277', 'Zefiro 284', 'Zefiro 285', 'Zefiro 294', 'Zefiro 295', 'Zefiro 665', 'Zefiro 675', 'Zefiro 685', 'Zefiro 690', 'Zefiro 695', 'Zefiro 696', 'Zefiro Sport', 'Zefiro Advance', 'Zefiro Plus', 'Zefiro TL', 'Zefiro M', 'Zefiro Integrale'].map((m, i) => (
                  <span key={i} className="bg-blue-50 px-3 py-1 rounded-full text-sm text-blue-700 border border-blue-200">{m}</span>
                ))}
              </div>
            </div>

            {/* T-Line Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">Low Profile</span>
                T-Line Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['T-Line 590', 'T-Line 700', 'T-Line 740', 'T-Line 743', 'T-Line 745', 'T-Line 750', 'T-Line 785', 'T-Line 590 S', 'T-Line 700 S', 'T-Line 740 S', 'T-Line Automatic', 'T-Line Manual'].map((m, i) => (
                  <span key={i} className="bg-green-50 px-3 py-1 rounded-full text-sm text-green-700 border border-green-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Pegaso Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">A-Class</span>
                Pegaso Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Pegaso 590', 'Pegaso 690', 'Pegaso 740', 'Pegaso 745', 'Pegaso 750', 'Pegaso 590 Integrale', 'Pegaso 740 Integrale', 'Pegaso 745 Integrale'].map((m, i) => (
                  <span key={i} className="bg-purple-50 px-3 py-1 rounded-full text-sm text-purple-700 border border-purple-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Auto-Roller Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">Family</span>
                Auto-Roller Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Auto-Roller 2', 'Auto-Roller 3', 'Auto-Roller 4', 'Auto-Roller 5', 'Auto-Roller 200', 'Auto-Roller 210', 'Auto-Roller 500', 'Auto-Roller 694', 'Auto-Roller 695', 'Auto-Roller 696', 'Auto-Roller 707', 'Auto-Roller 746', 'Auto-Roller 747', 'Auto-Roller 900', 'Auto-Roller Garage'].map((m, i) => (
                  <span key={i} className="bg-amber-50 px-3 py-1 rounded-full text-sm text-amber-700 border border-amber-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Kronos Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">Versatile</span>
                Kronos Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Kronos 265', 'Kronos 279', 'Kronos 284', 'Kronos 290', 'Kronos 295', 'Kronos 590', 'Kronos 665', 'Kronos Compact 590', 'Kronos Compact 665', 'Kronos TL', 'Kronos M'].map((m, i) => (
                  <span key={i} className="bg-red-50 px-3 py-1 rounded-full text-sm text-red-700 border border-red-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Granduca Range */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">Luxury</span>
                Granduca Range
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Granduca 265', 'Granduca 266', 'Granduca 267', 'Granduca 275', 'Granduca 277', 'Granduca 284', 'Granduca 285', 'Granduca 286', 'Granduca 295', 'Granduca Garage', 'Granduca Integrale'].map((m, i) => (
                  <span key={i} className="bg-indigo-50 px-3 py-1 rounded-full text-sm text-indigo-700 border border-indigo-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Livingstone & Campervans */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm">Compact</span>
                Livingstone, Toleno & Campervans
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Livingstone 2', 'Livingstone 5', 'Livingstone 6', 'Livingstone Sport', 'Toleno 2', 'Toleno 5', 'Toleno 6', 'Triaca 230', 'Triaca 232', 'Triaca 260'].map((m, i) => (
                  <span key={i} className="bg-teal-50 px-3 py-1 rounded-full text-sm text-teal-700 border border-teal-200">{m}</span>
                ))}
              </div>
            </div>

            {/* Historical/Discontinued Models */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <span className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm">2002-2015</span>
                Historical & Discontinued Models
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Atessa', 'Atessa 71', 'Atessa 590', 'Sirio 590', 'Sirio 599', 'Team 3', 'Team 4', 'Team 5', 'Team 6', 'Majestic', 'Starlight', 'Super Camper', 'Java', 'Elliot', 'Happy', 'Sfera', 'Sinfonia', 'Euromobil 540', 'Euromobil 590', 'Euromobil 600', 'Garage 6 Berth', 'First Class'].map((m, i) => (
                  <span key={i} className="bg-slate-100 px-3 py-1 rounded-full text-sm text-slate-600 border border-slate-300">{m}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-4">
              Cannot find your model listed? Contact us anyway - we can source parts for <strong>any Roller Team motorhome ever made</strong>.
            </p>
            <a href="https://wa.me/447527006966?text=Hi%2C%20I%20need%20parts%20for%20my%20Roller%20Team%20" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp us with your model
            </a>
          </div>
        </div>
      </section>

      {/* Why buy spares from us + how it works */}
      <section className="py-14 px-4 bg-slate-50" data-testid="parts-why-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why buy your Roller Team spares from us?</h2>
            <p className="text-slate-700 mb-4 leading-relaxed">We run a fleet of Roller Team motorhomes ourselves - Zefiro, T-Line and Auto-Roller - so we know these vehicles inside out. When a locker catch snaps, a window stay breaks or a blind cassette fails on one of ours, we source the genuine Roller Team part through the official UK channel, and we do exactly the same for you.</p>
            <p className="text-slate-700 mb-4 leading-relaxed">Unlike generic motorhome parts websites we can identify the correct Roller Team spare from a photo and your chassis number, check it against the build spec for your model year, and tell you honestly whether a genuine part, a Roller Team-approved alternative or a repair is the best option.</p>
            <ul className="space-y-2 text-slate-700">
              {['Genuine Roller Team parts, sourced through the official UK importer', 'Every model from 2002 to the current range, including discontinued models', 'Photo identification by WhatsApp - usually same day', 'UK-wide delivery, or collect from us in Suffolk', 'Fitting advice from people who maintain Roller Teams every week'].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How ordering Roller Team parts works</h2>
            <ol className="space-y-4">
              {[
                ['Find your chassis number', 'The 17-character VIN is on your V5C, the plate inside the cab door and often on the habitation door frame. It tells us the exact build.'],
                ['Send photos & a description', 'Use the enquiry form below or WhatsApp 07527 006966 - a photo of the part and one of where it fits is ideal.'],
                ['We identify & quote', 'We check the Roller Team parts catalogue for your model and year and send you a price and lead time, normally within one working day.'],
                ['Pay & we dispatch', 'In-stock items go out within 1-2 working days. Parts ordered from the Roller Team factory in Italy typically take around 8 weeks to arrive, so please allow for this when planning repairs.'],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4 bg-white rounded-xl p-4 border">
                  <div className="bg-red-500 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                  <div><p className="font-semibold text-slate-900">{t}</p><p className="text-sm text-slate-600">{d}</p></div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Popular parts */}
      <section className="py-14 px-4 bg-white" data-testid="parts-popular-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">Most requested Roller Team spare parts</h2>
          <p className="text-slate-600 text-center max-w-3xl mx-auto mb-8">These are the Roller Team spares we are asked for most often. If yours isn't listed, we can almost certainly still get it.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ['Locker doors, hinges & catches', 'Garage and external locker doors, Zadi/FAP locks, gas struts and keys cut to code.'],
              ['Windows, stays & blinds', 'Seitz / Dometic windows, roof lights, window stays, blind and flyscreen cassettes.'],
              ['Habitation door parts', 'Door handles, locks, cassettes, seals, flyscreen doors and steps.'],
              ['Body trims, graphics & mouldings', 'Side skirts, wheel-arch trims, rear light clusters, awning rails and decals.'],
              ['Interior furniture & fittings', 'Table legs, cupboard catches, hinges, drop-down bed parts, cushions and upholstery.'],
              ['Water, gas & electrical', 'Pumps, taps, tank sensors, control panels, fuse boards, chargers and leisure battery parts.'],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border p-5 hover:shadow-md transition-shadow"><h3 className="font-semibold text-slate-900 mb-1">{t}</h3><p className="text-sm text-slate-600">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Parts Enquiry Form</h2>
          <p className="text-slate-600 text-center mb-8">Fill in the details below and we will get back to you with pricing and availability.</p>

          {submitStatus && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${submitStatus.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'} border`}>
              {submitStatus.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              <p>{submitStatus.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-blue-600" />Your Contact Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Full Name *</Label><Input name="customerName" value={formData.customerName} onChange={handleInputChange} required /></div>
                  <div><Label>Phone *</Label><Input name="customerPhone" type="tel" value={formData.customerPhone} onChange={handleInputChange} required /></div>
                </div>
                <div><Label>Email *</Label><Input name="customerEmail" type="email" value={formData.customerEmail} onChange={handleInputChange} required /></div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-green-600" />Vehicle Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Chassis Number (VIN) *</Label><Input name="chassisNumber" value={formData.chassisNumber} onChange={handleInputChange} className="font-mono" required /><p className="text-xs text-slate-500 mt-1">Found on vehicle plate or registration document</p></div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><Label>Make *</Label><Input name="vehicleMake" value={formData.vehicleMake} onChange={handleInputChange} placeholder="e.g. Roller Team" required /></div>
                  <div><Label>Model *</Label><Input name="vehicleModel" value={formData.vehicleModel} onChange={handleInputChange} placeholder="e.g. Zefiro 675" required /></div>
                  <div><Label>Year *</Label><Input name="vehicleYear" value={formData.vehicleYear} onChange={handleInputChange} placeholder="e.g. 2023" required /></div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Wrench className="h-5 w-5 text-amber-600" />Parts Required</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Describe the parts you need *</Label>
                  <textarea name="partsDescription" value={formData.partsDescription} onChange={handleInputChange} placeholder="Describe parts needed, include part numbers if known..." className="w-full p-3 border rounded-lg min-h-[120px]" required />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2"><Camera className="h-4 w-4" />Photos (Optional - up to 5)</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id="photo-upload" disabled={photos.length >= 5} />
                    <label htmlFor="photo-upload" className="cursor-pointer"><Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" /><p className="text-slate-600">{photos.length >= 5 ? 'Max 5 photos' : 'Click to upload photos'}</p></label>
                  </div>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-4">
                      {photos.map((photo, i) => (
                        <div key={i} className="relative group">
                          <img src={URL.createObjectURL(photo)} alt="" className="w-full h-16 object-cover rounded" />
                          <button type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" disabled={!isFormValid || isSubmitting} className="w-full py-6 text-lg bg-red-600 hover:bg-red-700">
              {isSubmitting ? 'Submitting...' : 'Submit Parts Enquiry'}
            </Button>
          </form>

          {/* WhatsApp CTA */}
          <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="text-center sm:text-left">
                <p className="font-semibold text-green-800 text-lg">Prefer WhatsApp?</p>
                <p className="text-green-700 text-sm">Send photos and get quick advice</p>
              </div>
              <a href="https://wa.me/447527006966?text=Hi%2C%20I%27m%20looking%20for%20Roller%20Team%20parts." target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-full">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Us
              </a>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-600 mb-3">Or call us:</p>
            <a href="tel:07527006966" className="text-blue-600 hover:text-blue-800 font-medium text-lg"><Phone className="h-5 w-5 inline mr-2" />07527 006966</a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "Where can I buy genuine Roller Team parts in the UK?", a: "Millie's Motorhome Hire in Suffolk is a trusted supplier of genuine Roller Team parts. We offer fast UK delivery and expert advice. Submit an enquiry or WhatsApp us photos for quick identification." },
              { q: "How do I find the right part for my Roller Team motorhome?", a: "You'll need your chassis number (VIN), make, model, and year. This is found on the vehicle plate or registration document. WhatsApp us photos for even faster identification." },
              { q: "What Roller Team models do you supply parts for?", a: "We supply parts for ALL Roller Team models from 2002 to present: Zefiro (265-696, Sport, Advance), T-Line (590-785), Pegaso (590-750), Auto-Roller (2-900), Kronos (265-665, Compact), Granduca (265-295), Livingstone, Toleno, plus discontinued models like Atessa, Sirio, Team, Majestic, Starlight, and more." },
              { q: "How long does delivery take?", a: "Most in-stock parts dispatch within 1-2 working days. Parts that have to be ordered from the Roller Team factory in Italy typically take around 8 weeks to arrive - we will confirm the expected lead time with your quote." },
              { q: "Are your parts genuine Roller Team parts?", a: "Yes, 100% genuine Roller Team parts sourced through official channels. We never sell pattern or aftermarket parts as genuine." },
              { q: "Do you supply Roller Team spares to the whole of the UK?", a: "Yes - we post Roller Team spare parts to every part of the UK, and customers in Suffolk, Essex, Norfolk and Cambridgeshire are welcome to collect from us at Wattisham." },
              { q: "Can you help identify a part from a photo?", a: "Absolutely - WhatsApp a photo of the part, a photo of where it fits and your chassis number to 07527 006966 and we will usually identify it the same day." },
              { q: "Can you get parts for older Roller Team motorhomes?", a: "Yes! We can source parts for any Roller Team motorhome from 2002 onwards, including discontinued models like Atessa, Sirio, Team, Majestic, Starlight, Super Camper, Java, Elliot, and more. Contact us with your chassis number." }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 px-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Need Roller Team Parts? Get in Touch Today</h2>
          <p className="text-slate-300 mb-6">Fast UK delivery and expert advice guaranteed.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-full font-semibold">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp Us
            </a>
            <a href="tel:07527006966" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-full font-semibold border border-white/30">
              <Phone className="h-5 w-5" />Call 07527 006966
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RollerTeamParts;
